import { Box, Link, Separator, Stack, Text, Wrap, WrapItem } from '@chakra-ui/react';
import * as React from 'react';
import NextLink from 'next/link';
import { useTranslation } from 'react-i18next';
import { CopyButton } from '@/components/copyButton';
import { useOrganisationState } from '@/contexts/OrganisationContext';
import { Logo } from './Logo';
import { AustralianAccreditedLogo } from './AustralianAccreditedLogo';
import { GenaLogo } from './GenaLogo';
import { VerifactuCertifiedLogo } from './VerifactuCertifiedLogo';
import { PdpReadyLogo } from './PdpReady';
import { PeppolCertifiedLogo } from './PeppolCertifiedLogo';

interface FooterLink {
  titleKey: string;
  url: string;
}

interface FooterProps {
  reserveOrganisationId?: boolean;
}

const links: { product: FooterLink[]; resources: FooterLink[]; legal: FooterLink[] } = {
  product: [
    { titleKey: 'footer.features', url: '/#features' },
    { titleKey: 'footer.howItWorks', url: '/#how-it-works' },
    { titleKey: 'footer.contactUs', url: '/#contact-us' },
    { titleKey: 'footer.faqs', url: '/#faqs' }
  ],
  resources: [
    { titleKey: 'footer.blog', url: 'https://blog.eezi.io' },
    { titleKey: 'footer.documentation', url: 'https://docs.eezi.io/docs/overview' },
    { titleKey: 'footer.status', url: 'https://eezi.instatus.com' }
  ],
  legal: [
    { titleKey: 'footer.termsOfService', url: '/legal/terms/' },
    { titleKey: 'footer.privacyPolicy', url: '/legal/privacy/' },
    { titleKey: 'footer.verifactuDeclaration', url: '/legal/verifactu/' }
  ]
};

export const Footer = ({ reserveOrganisationId = false }: FooterProps) => {
  const { t } = useTranslation('common');
  const { currentOrganisation } = useOrganisationState();
  const organisationId = currentOrganisation?.id;

  return (
    <Box px={{ base: '0', md: '4' }}>
      <Stack gap='8' direction={{ base: 'column', md: 'row' }} justify='space-between' py={{ base: '12', md: '16' }}>
        <Stack gap={{ base: '6', md: '8' }} align='start'>
          <Logo h='100px' mx={{ base: 'auto', sm: 'inherit' }} />
        </Stack>

        <Stack direction={{ base: 'column-reverse', md: 'column', lg: 'row' }} gap={{ base: '12', md: '8' }}>
          <Stack
            direction={{ base: 'column-reverse', sm: 'row', md: 'row', lg: 'row' }}
            gap='8'
            mx={{ base: 'auto', lg: '' }}
          >
            <Stack gap='4' minW='36' flex='1'>
              <Text fontSize='sm' fontWeight='bold' _dark={{ color: 'whiteAlpha.600' }}>
                {t('footer.product')}
              </Text>
              <Stack gap='3'>
                {links.product.map((link) => (
                  <Link
                    asChild
                    key={link.titleKey}
                    href={link.url}
                    color='gray.600'
                    _dark={{ color: 'whiteAlpha.900' }}
                  >
                    <NextLink href={link.url} key={link.titleKey}>
                      {t(link.titleKey)}
                    </NextLink>
                  </Link>
                ))}
              </Stack>
            </Stack>
            <Stack gap='4' minW='36' flex='1'>
              <Text fontSize='sm' fontWeight='bold' _dark={{ color: 'whiteAlpha.600' }}>
                {t('footer.resources')}
              </Text>
              <Stack gap='3'>
                {links.resources.map((link) => (
                  <Link
                    asChild
                    key={link.titleKey}
                    href={link.url}
                    color='gray.600'
                    _dark={{ color: 'whiteAlpha.900' }}
                    minWidth='auto'
                  >
                    <NextLink href={link.url}>{t(link.titleKey)}</NextLink>
                  </Link>
                ))}
              </Stack>
            </Stack>
            <Stack gap='4' minW='36' flex='1'>
              <Text fontSize='sm' fontWeight='bold' _dark={{ color: 'whiteAlpha.600' }}>
                {t('footer.legal')}
              </Text>
              <Stack gap='3'>
                {links.legal.map((link) => (
                  <Link
                    asChild
                    key={link.titleKey}
                    href={link.url}
                    color='gray.600'
                    _dark={{ color: 'whiteAlpha.900' }}
                  >
                    <NextLink href={link.url}>{t(link.titleKey)}</NextLink>
                  </Link>
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Wrap
        gap={{ base: '6', sm: '8' }}
        direction={{ base: 'column', sm: 'row' }}
        width='100%'
        align={'center'}
        pb={{ base: '12', md: '16' }}
        justifyContent={{ base: 'center', md: 'space-between' }}
      >
        <WrapItem>
          <PeppolCertifiedLogo color='#5d6f82' h='48px' mx={{ base: 'auto', sm: 'inherit' }} />
        </WrapItem>
        <WrapItem>
          <GenaLogo color='#5d6f82' h='44px' mx={{ base: 'auto', sm: 'inherit' }} />
        </WrapItem>
        <WrapItem>
          <AustralianAccreditedLogo color='#5d6f82' h='56px' mx={{ base: 'auto', sm: 'inherit' }} />
        </WrapItem>
        <WrapItem>
          <PdpReadyLogo color='#5d6f82' h='52px' mx={{ base: 'auto', sm: 'inherit' }} />
        </WrapItem>
        <WrapItem>
          <VerifactuCertifiedLogo color='#5d6f82' h='58px' mx={{ base: 'auto', sm: 'inherit' }} />
        </WrapItem>
      </Wrap>

      <Separator />
      <Stack pt='8' pb='4' justify='space-between' direction={{ base: 'column', md: 'row' }} align='center'>
        <Text fontSize='sm' color='gray.400' _dark={{ color: 'secondary.300' }}>
          &copy;
          {`${new Date().getFullYear()} `}
          {t('footer.allRightsReserved')}
        </Text>
        {(organisationId || reserveOrganisationId) && (
          <Stack
            justify='space-between'
            direction={{ base: 'column', md: 'row' }}
            align='center'
            visibility={organisationId ? 'visible' : 'hidden'}
            aria-hidden={!organisationId}
          >
            <Text fontSize='sm' color='gray.400' _dark={{ color: 'secondary.300' }}>
              {t('footer.organisationId')}
            </Text>
            <Text fontSize='sm' color='gray.400' _dark={{ color: 'secondary.300' }}>
              {organisationId || 'Organisation ID'}
            </Text>
            <Box fontSize='sm' color='gray.400' _dark={{ color: 'secondary.300' }}>
              {organisationId ? (
                <CopyButton copyVal={organisationId} color='gray.400' _dark={{ color: 'secondary.300' }} />
              ) : (
                <Box boxSize='8' />
              )}
            </Box>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
